"use client";

import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from "@tanstack/react-query";
import { prepareProviderMemory } from "@/lib/auth/last-sign-in-method";
import { authClient } from "@/lib/auth-client";
import { logger } from "@/lib/logger";

/**
 * OAuth Client Info
 */
export interface OAuthClientInfo {
  clientId: string;
  clientName: string;
  ownerBapId: string;
  /** Ordinal origin (txid_vout) of a ThemeToken inscription for white-label theming */
  themeOrigin: string | null;
  /** URL to client logo/icon */
  logoUrl: string | null;
}
export function useOAuthClientInfo(clientId, initialData) {
  const $ = _c(9);
  let t0;
  if ($[0] !== clientId) {
    t0 = ["oauth-client-info", clientId];
    $[0] = clientId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = initialData && initialData.clientId === clientId ? initialData : undefined;
  let t2;
  if ($[2] !== clientId) {
    t2 = async () => {
      if (!clientId) {
        return null;
      }
      const response = await fetch(`/api/oauth-clients?clientId=${encodeURIComponent(clientId)}`);
      if (response.status === 404) {
        return null;
      }
      if (!response.ok) {
        throw new Error("Failed to fetch OAuth client info");
      }
      const data = await response.json();
      const client = data.clients?.[0];
      if (!client) {
        return null;
      }
      return {
        clientId: client.clientId,
        clientName: client.name,
        ownerBapId: client.ownerBapId,
        themeOrigin: client.themeOrigin ?? null,
        logoUrl: client.logoUri ?? client.icon ?? null
      };
    };
    $[2] = clientId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = !!clientId;
  let t4;
  if ($[4] !== t0 || $[5] !== t1 || $[6] !== t2 || $[7] !== t3) {
    t4 = {
      queryKey: t0,
      initialData: t1,
      queryFn: t2,
      enabled: t3,
      staleTime: 300000,
      retry: false
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return useQuery(t4);
}

/**
 * Social Sign-In (GitHub, Google, Apple, Twitter, Discord)
 * Uses Better Auth client directly for proper callbackURL handling
 * including newUserCallbackURL for first-time registrations
 */
interface SocialSignInInput {
  provider: "github" | "google" | "apple" | "twitter" | "discord";
  callbackURL: string;
}
export function useSocialSignIn() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp,
      onError: _temp2
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
function _temp2(error) {
  logger.error({
    error
  }, "Social sign-in failed");
}
async function _temp(input) {
  const result = await authClient.signIn.social({
    provider: input.provider,
    ...prepareProviderMemory(input.provider, input.callbackURL, "/onboarding?newAccount=1"),
    requestSignUp: true
  });
  if (result.error) {
    throw new Error(result.error.message || "Social sign-in failed");
  }
  return result;
}